import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, Sparkles } from 'lucide-react';
import { Fireflies, Moth } from '../components/FairyElements';
import { fetchBlogs, resolveImageUrl } from '../services/api';

const Blog = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs().then(setPosts).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1612]">
      <section className="forest-bg min-h-[500px] flex items-center px-4 md:px-8 lg:px-16 py-20" style={{ '--forest-image': "url(/mystical-garden/blog-library.png)", '--bg-opacity': 0.65}}>
        <Fireflies count={40} />
        <Moth delay={0} top="30%" />

        <div className="max-w-6xl mx-auto w-full text-center relative z-10 animate-fadeIn">
          <Badge className="mb-4 bg-emerald-900/60 border border-amber-500/40 text-amber-300 backdrop-blur-md">
            <Sparkles className="w-3 h-3 mr-1.5" />
            <span className="tracking-widest text-xs uppercase">Wisdom & Insights</span>
          </Badge>
          <h1 className="text-6xl md:text-7xl font-bold mb-6 gradient-text text-glow">Spiritual Blog</h1>
          <p className="text-lg md:text-xl text-amber-100/80 max-w-3xl mx-auto font-serif-mystical italic">
            Insights, wisdom, and guidance from the mystical garden of spiritual knowledge.
          </p>
        </div>
      </section>

      <section className="section-deep py-24 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={25} />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <p className="text-amber-100/60 col-span-full text-center py-12">Loading articles...</p>
            ) : posts.length === 0 ? (
              <p className="text-amber-100/60 col-span-full text-center py-12">No articles yet. Check back soon!</p>
            ) : posts.map((post) => (
              <Card
                key={post.id}
                className="forest-card border-0 overflow-hidden group cursor-pointer"
                onClick={() => navigate(`/blog/${post.id}`)}
                data-testid={`blog-card-${post.id}`}
              >
                <div className="h-56 overflow-hidden bg-emerald-900/40 relative">
                  <img
                    src={resolveImageUrl(post.image)}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-transparent to-transparent" />
                </div>

                <CardHeader>
                  <div className="flex justify-between items-center mb-3">
                    <Badge className="bg-emerald-800/80 text-amber-300 border border-amber-500/30">{post.category}</Badge>
                    <div className="flex items-center text-xs text-amber-100/60">
                      <Clock className="w-3 h-3 mr-1" />
                      {post.readTime}
                    </div>
                  </div>
                  <CardTitle className="text-xl text-amber-200 group-hover:text-amber-300 transition-colors" style={{ fontFamily: 'Cinzel, serif' }}>{post.title}</CardTitle>
                  <CardDescription className="text-amber-100/70">{post.excerpt}</CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="flex items-center text-sm text-amber-100/60">
                    <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                    {new Date(post.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="forest-bg py-20 px-4 md:px-8 lg:px-16 relative" style={{ '--forest-image': "url(/mystical-garden/blog-library.png)", '--bg-opacity': 0.3}}>
        <Fireflies count={15} />
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-4xl font-bold text-center mb-10 gradient-text">Explore by Category</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {['Tarot Basics', 'Angel Cards', 'Crystal Healing', 'Spiritual Practice', 'Love & Relationships', 'Career Guidance'].map((category) => (
              <Badge
                key={category}
                className="text-base py-2 px-5 cursor-pointer bg-emerald-900/60 border border-amber-500/40 text-amber-300 hover:bg-emerald-800 hover:border-amber-400 transition-all backdrop-blur-md"
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
